import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { IndianRupee, Plus } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { DialogEyebrow } from '@/components/common/DialogPrimitives';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { paymentsService } from '@/services/payments';
import { refundsService, type CreateRefundPayload } from '@/services/refunds';
import { fmt } from '@/lib/format';
import type { SalesOrder } from '@/types/orders';
import type { RefundType } from '@/types/refunds';

interface Props {
  /** Sales order to refund against (FE always knows the order id at this point). */
  order: SalesOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Refund creation form. Three flavours per the BE `RefundType` enum:
 *
 *   FULL          — refund the entire captured amount; defaults to the
 *                   sum of all SUCCESS payments on the order.
 *   PARTIAL       — operator-entered amount (less than total captured).
 *   STORE_CREDIT  — non-cash refund credited to the customer's wallet;
 *                   no gateway call, just an accounting entry.
 *
 * The BE auto-creates a corresponding RefundEvent and (for gateway types)
 * triggers the provider refund call. The status moves REQUESTED →
 * APPROVED → INITIATED → PROCESSED async via webhook callbacks.
 */
export function RefundInitiateDialog({ order, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [type, setType] = useState<RefundType>('FULL');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [paymentId, setPaymentId] = useState<string>('__auto');

  // Pull the order's payments so the operator can target a specific
  // gateway charge (defaults to the most recent SUCCESS payment).
  const paymentsQuery = useQuery({
    queryKey: ['payments', 'forOrder', order?.id],
    queryFn: () => paymentsService.list({ salesOrderId: order!.id, pageSize: 50 }),
    enabled: !!order && open,
  });

  const successPayments =
    paymentsQuery.data?.rows.filter((p) => p.status === 'SUCCESS') ?? [];
  const captured = successPayments.reduce((s, p) => s + Number(p.amount), 0);

  useEffect(() => {
    if (!open) return;
    setType('FULL');
    setAmount(captured > 0 ? String(captured) : '');
    setReason('');
    setPaymentId('__auto');
  }, [open, captured]);

  // Auto-update amount field when type flips between FULL / PARTIAL.
  useEffect(() => {
    if (type === 'FULL' && captured > 0) setAmount(String(captured));
  }, [type, captured]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: CreateRefundPayload = {
        salesOrderId: order!.id,
        paymentId: paymentId !== '__auto' ? paymentId : successPayments[0]?.id,
        type,
        amount: Number(amount),
        currency: order?.currency ?? 'INR',
        reason: reason.trim() || undefined,
      };
      return refundsService.create(payload);
    },
    onSuccess: (refund) => {
      toast.success(`Refund ${refund.refundNumber} requested`);
      qc.invalidateQueries({ queryKey: ['refunds'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not create refund'),
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!order || !amount || Number(amount) <= 0) return;
    mutation.mutate();
  };

  const amountNum = Number(amount);
  const overflow = amountNum > captured && type !== 'STORE_CREDIT' && captured > 0;

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      headerSlot={
        <div className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
          >
            <Plus className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1 space-y-1.5">
            <DialogEyebrow>
              New refund · {order?.orderNumber ?? ''}
            </DialogEyebrow>
            <DialogTitle className="text-[18px] font-semibold leading-tight tracking-tight">
              Issue a refund
            </DialogTitle>
            <DialogDescription className="text-[12.5px] leading-relaxed">
              Pick the refund type, confirm the amount, and submit. The BE
              triggers the provider refund call automatically; status moves
              REQUESTED → APPROVED → INITIATED → PROCESSED via webhook.
            </DialogDescription>
          </div>
        </div>
      }
      title=""
      submitLabel="Request refund"
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="2xl"
    >
      <FormField label="Type" htmlFor="refundType" required>
        <Select value={type} onValueChange={(v) => setType(v as RefundType)}>
          <SelectTrigger id="refundType" className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="FULL">Full — refund entire captured amount</SelectItem>
            <SelectItem value="PARTIAL">Partial — refund a specific amount</SelectItem>
            <SelectItem value="STORE_CREDIT">Store credit — non-cash wallet credit</SelectItem>
          </SelectContent>
        </Select>
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          label="Amount"
          htmlFor="amount"
          required
          hint={
            captured > 0
              ? `Captured: ${fmt.inr(captured)} across ${successPayments.length} payment${successPayments.length === 1 ? '' : 's'}`
              : 'No captured payments on this order'
          }
        >
          <Input
            id="amount"
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className="h-9 text-right font-mono tabular-nums"
            disabled={type === 'FULL' && captured > 0}
            required
          />
        </FormField>
        <FormField label="Currency" htmlFor="currency">
          <Input id="currency" value={order?.currency ?? 'INR'} disabled className="h-9 font-mono" />
        </FormField>
      </div>

      {type !== 'STORE_CREDIT' && successPayments.length > 0 && (
        <FormField
          label="Against payment"
          htmlFor="paymentId"
          hint="Defaults to the most recent successful payment."
        >
          <Select value={paymentId} onValueChange={setPaymentId}>
            <SelectTrigger id="paymentId" className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__auto">Auto — most recent SUCCESS</SelectItem>
              {successPayments.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  <span className="font-mono">{p.paymentNumber}</span>
                  <span className="ml-2 text-muted-foreground">
                    {fmt.inr(Number(p.amount))} · {p.provider}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      )}

      <FormField
        label="Reason"
        htmlFor="reason"
        hint="Optional — appears on the refund record and audit trail."
      >
        <Textarea
          id="reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Customer cancelled — never shipped"
          rows={2}
        />
      </FormField>

      {overflow && (
        <div className="flex items-center gap-2 rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-[12px] text-[hsl(38_85%_30%)]">
          <IndianRupee className="h-3.5 w-3.5 shrink-0" />
          Refund amount {fmt.inr(amountNum)} exceeds captured {fmt.inr(captured)}. The BE
          will reject this for non-store-credit refunds.
        </div>
      )}
    </FormSheet>
  );
}
